import { useRef, useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import {
  type Societa,
  indirizzoFattura,
  useAttaccaFattura,
  useStaccaFattura,
} from '../api/fatture-ordine.js';
import type { Ordine } from '../api/ordini.js';
import { t } from '../i18n/lingua.js';

/**
 * Il PDF di una delle due fatture.
 *
 * Una riga sola: o c'è il file e lo si scarica, o non c'è e lo si carica.
 * Nel v2 è una finestra a parte con due zone di trascinamento e un pulsante
 * Conferma, che si apre da un pulsante nella barra in fondo — quattro clic
 * per attaccare un PDF che si ha già in mano.
 */
export function FatturaPdf({ ordine, societa }: { ordine: Ordine; societa: Societa }) {
  const attacca = useAttaccaFattura(ordine.id);
  const stacca = useStaccaFattura(ordine.id);
  const campo = useRef<HTMLInputElement>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const [staccando, setStaccando] = useState(false);

  const nome = societa === 'spa' ? ordine.invoiceFileNameSpa : ordine.invoiceFileNameInc;
  const etichetta = societa === 'spa' ? 'SPA' : 'Inc';
  const inCorso = attacca.isPending || stacca.isPending;

  const scegli = (file: File | null) => {
    setProblema(null);
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setProblema(t('La fattura si carica in PDF.'));
      return;
    }
    attacca.mutate(
      { file, issuer: societa },
      {
        onError: (errore) =>
          setProblema(
            errore instanceof ErroreApi ? errore.message : t('Il caricamento non è riuscito.'),
          ),
      },
    );
  };

  return (
    <div className="riga-file">
      <span className="dim" style={{ minWidth: 62 }}>
        {t('PDF')} {etichetta}
      </span>

      {nome ? (
        <>
          <a
            className="cell-link"
            href={indirizzoFattura(ordine.id, societa)}
            style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {nome}
          </a>
          {!ordine.archived &&
            (staccando ? (
              <>
                <button className="btn btn-ghost" onClick={() => setStaccando(false)}>
                  {t('Lascia stare')}
                </button>
                <button
                  className="btn btn-danger"
                  disabled={inCorso}
                  onClick={() => {
                    stacca.mutate(societa);
                    setStaccando(false);
                  }}
                >
                  {t('Stacca')}
                </button>
              </>
            ) : (
              <button className="btn btn-ghost" onClick={() => setStaccando(true)}>
                {t('Stacca')}
              </button>
            ))}
        </>
      ) : (
        <>
          <span className="dim" style={{ flex: 1 }}>
            {t('nessun PDF')}
          </span>
          {!ordine.archived && (
            <button className="btn btn-ghost" disabled={inCorso} onClick={() => campo.current?.click()}>
              {attacca.isPending ? t('Carico…') : t('Carica')}
            </button>
          )}
        </>
      )}

      <input
        ref={campo}
        type="file"
        accept=".pdf,application/pdf"
        hidden
        onChange={(e) => {
          scegli(e.target.files?.[0] ?? null);
          e.target.value = '';
        }}
      />

      {problema && (
        <span className="dim" style={{ fontSize: 12, color: 'var(--tomato)' }}>
          {problema}
        </span>
      )}
    </div>
  );
}
